import { useEffect, useRef, useCallback } from "react";
import type { MapRef } from "react-map-gl/maplibre";
import { useEditorStore } from "@/store";
import { MAP_DEFAULTS } from "@/styles/theme";

const SOURCE_ID = "__editor-initial-view";
const CENTER_LAYER_ID = "__editor-initial-center";
const BOUNDS_LAYER_ID = "__editor-initial-bounds";

/**
 * Shows the initial view center + approximate viewport bounds on the map
 * when the Map (root) section is active.
 */
export function useInitialViewOverlay(mapRef: React.RefObject<MapRef | null>) {
  const activeSection = useEditorStore((s) => s.activeSection);
  const editorMode = useEditorStore((s) => s.editorMode);
  const style = useEditorStore((s) => s.style);
  const isActive = editorMode === "sections" && activeSection === "root";
  const prevActive = useRef(false);
  const retryTimer = useRef<ReturnType<typeof setTimeout>>(undefined);

  const addOverlay = useCallback((map: maplibregl.Map) => {
    const center = Array.isArray(style.center)
      ? (style.center as number[])
      : [...MAP_DEFAULTS.center];
    const lng = (center[0] as number) ?? 0;
    const lat = (center[1] as number) ?? 0;
    const zoom = (typeof style.zoom === "number" ? style.zoom : MAP_DEFAULTS.zoom) as number;

    const viewDeg = 360 / Math.pow(2, zoom);
    const halfW = viewDeg / 2;
    const halfH = viewDeg / 3;

    const geojson: GeoJSON.FeatureCollection = {
      type: "FeatureCollection",
      features: [
        {
          type: "Feature",
          properties: {},
          geometry: {
            type: "Polygon",
            coordinates: [[
              [lng - halfW, lat - halfH],
              [lng + halfW, lat - halfH],
              [lng + halfW, lat + halfH],
              [lng - halfW, lat + halfH],
              [lng - halfW, lat - halfH],
            ]],
          },
        },
        {
          type: "Feature",
          properties: {},
          geometry: { type: "Point", coordinates: [lng, lat] },
        },
      ],
    };

    try {
      // If source exists, just update data
      const existing = map.getSource(SOURCE_ID) as maplibregl.GeoJSONSource | undefined;
      if (existing && typeof existing.setData === "function") {
        existing.setData(geojson);
        return;
      }

      // Remove stale remnants if any
      removeOverlay(map);

      // Add fresh
      map.addSource(SOURCE_ID, { type: "geojson", data: geojson });
      map.addLayer({
        id: BOUNDS_LAYER_ID,
        type: "fill",
        source: SOURCE_ID,
        filter: ["==", "$type", "Polygon"],
        paint: { "fill-color": "#6366f1", "fill-opacity": 0.15 },
      });
      map.addLayer({
        id: `${BOUNDS_LAYER_ID}-outline`,
        type: "line",
        source: SOURCE_ID,
        filter: ["==", "$type", "Polygon"],
        paint: { "line-color": "#6366f1", "line-width": 3, "line-opacity": 0.8 },
      });
      map.addLayer({
        id: CENTER_LAYER_ID,
        type: "circle",
        source: SOURCE_ID,
        filter: ["==", "$type", "Point"],
        paint: {
          "circle-radius": 10,
          "circle-color": "#6366f1",
          "circle-stroke-color": "#ffffff",
          "circle-stroke-width": 3,
          "circle-opacity": 0.95,
        },
      });
    } catch {
      // Style may be mid-update — retry shortly
      clearTimeout(retryTimer.current);
      retryTimer.current = setTimeout(() => {
        try { addOverlay(map); } catch { /* give up */ }
      }, 300);
    }
  }, [style.center, style.zoom]);

  useEffect(() => {
    const map = mapRef.current?.getMap();
    if (!map) return;

    if (!isActive) {
      if (prevActive.current) {
        removeOverlay(map);
        prevActive.current = false;
      }
      clearTimeout(retryTimer.current);
      return;
    }

    prevActive.current = true;

    // Add overlay now if style is loaded
    if (map.isStyleLoaded()) {
      addOverlay(map);
    }

    // Re-add after any style reload (setStyle with diff removes our layers)
    const onStyleData = () => {
      if (!prevActive.current) return;
      // Small delay to let the style fully settle
      clearTimeout(retryTimer.current);
      retryTimer.current = setTimeout(() => {
        if (prevActive.current) addOverlay(map);
      }, 100);
    };

    map.on("styledata", onStyleData);

    return () => {
      map.off("styledata", onStyleData);
      clearTimeout(retryTimer.current);
    };
  }, [isActive, style.center, style.zoom, style.pitch, style.bearing, mapRef, addOverlay]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      const map = mapRef.current?.getMap();
      if (map) removeOverlay(map);
      clearTimeout(retryTimer.current);
    };
  }, [mapRef]);
}

function removeOverlay(map: maplibregl.Map) {
  try {
    if (map.getLayer(CENTER_LAYER_ID)) map.removeLayer(CENTER_LAYER_ID);
    if (map.getLayer(BOUNDS_LAYER_ID)) map.removeLayer(BOUNDS_LAYER_ID);
    if (map.getLayer(`${BOUNDS_LAYER_ID}-outline`)) map.removeLayer(`${BOUNDS_LAYER_ID}-outline`);
    if (map.getSource(SOURCE_ID)) map.removeSource(SOURCE_ID);
  } catch {
    // Already cleaned
  }
}
